/**
 * VÉRAULT Member Suite — Experience · Essentials (Wave 27.1 · Phase 4).
 * ────────────────────────────────────────────────────────────────────
 * The operational sanctuary. Transportation · Stays · Balances ·
 * Agreements · Travel Documents — all in one calm editorial hub.
 *
 *   Feels: organized · calm · high-touch · luxurious · mission-critical.
 *   NEVER: file cabinet · accounting software · Dropbox.
 *
 * Pull-once aggregator: GET /experiences/{id}/essentials returns every
 * shelf in one round-trip. Outstanding items surface at top + inside
 * Overview via deep-links (Wave 26.27 useArrivalFocus pulse).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, BedDouble, Wallet, FileSignature, FileText, Compass, UtensilsCrossed } from "lucide-react";
import { EditorialRule, motion as portalMotion, api } from "../../../index";
import { useArrivalFocus } from "../../../../hooks/useArrivalFocus";
import TransportationShelf from "./editorial/TransportationShelf";
import StaysShelf from "./editorial/StaysShelf";
import BalancesShelf from "./editorial/BalancesShelf";
import BudgetShelf from "./editorial/BudgetShelf";
import ActivitiesShelf from "./editorial/ActivitiesShelf";
import AgreementsShelf from "./editorial/AgreementsShelf";
import TravelDocumentsShelf from "./editorial/TravelDocumentsShelf";
import OutstandingItemsBand from "./editorial/OutstandingItemsBand";

const { fadeInUp, stagger } = portalMotion;

const SECTION_NAV = [
  { id: "transportation", label: "Transportation", Icon: Plane },
  { id: "stays", label: "Stays", Icon: BedDouble },
  { id: "restaurants", label: "Restaurants", Icon: UtensilsCrossed },
  { id: "budget", label: "Trip budget", Icon: Wallet },
  { id: "activities", label: "Activities", Icon: Compass },
  { id: "balances", label: "Balances", Icon: Wallet },
  { id: "agreements", label: "Agreements", Icon: FileSignature },
  { id: "travel-documents", label: "Travel docs", Icon: FileText },
];

function SectionPills({ available }) {
  // Only show pills for sections that actually have content.
  const visible = SECTION_NAV.filter((s) => available.has(s.id));
  if (visible.length === 0) return null;
  return (
    <nav
      aria-label="Essentials sections"
      className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto no-scrollbar"
      data-testid="essentials-section-pills"
    >
      <ul className="flex gap-2 min-w-max">
        {visible.map((s) => {
          const { Icon } = s;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] bg-white/60 border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors"
                data-testid={`essentials-pill-${s.id}`}
              >
                <Icon className="size-3" strokeWidth={1.4} />
                {s.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default function ExperienceEssentials() {
  const { trip } = useOutletContext() || {};
  useArrivalFocus();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchEssentials = useCallback(() => {
    if (!trip?.id) return Promise.resolve();
    return api.get(`/client-portal/experiences/${trip.id}/essentials`)
      .then((res) => {
        // Wave 30 · Phase 7c — toast is now emitted by useLiveSync at the
        // ExperienceLayout level (de-duplicated across all sections). We
        // no longer toast here on per-page poll refetch.
        setData(res.data);
      })
      .catch((err) => setError(err));
  }, [trip?.id]);

  useEffect(() => {
    if (!trip?.id) return undefined;
    setError(null);
    fetchEssentials();

    // Wave 30 · Phase 7c — listen to centralized live-sync events.
    // The old per-page 60s interval is removed (useLiveSync at the
    // ExperienceLayout level drives this).
    const onEssentialsSync = () => {
      if (document.visibilityState === "visible") fetchEssentials();
    };
    const onBalancesSync = () => {
      if (document.visibilityState === "visible") fetchEssentials();
    };
    window.addEventListener("vt:sync-changed:essentials", onEssentialsSync);
    window.addEventListener("vt:sync-changed:balances", onBalancesSync);
    // Refetch on tab focus for immediate catch-up.
    const onVis = () => {
      if (document.visibilityState === "visible") fetchEssentials();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("vt:sync-changed:essentials", onEssentialsSync);
      window.removeEventListener("vt:sync-changed:balances", onBalancesSync);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [trip?.id, fetchEssentials]);

  const [hasActivities, setHasActivities] = useState(false);
  const [hasReservations, setHasReservations] = useState(false);
  const available = useMemo(() => {
    const s = new Set();
    if (!data) return s;
    if (data.transportation?.length) s.add("transportation");
    if (data.stays?.length) s.add("stays");
    if (data.budget?.line_items?.length || data.budget?.summary || data.budget?.categories?.length) s.add("budget");
    if (data.balances?.items?.length || data.balances?.experience_total) s.add("balances");
    if (data.agreements?.length) s.add("agreements");
    if ((data.travel_documents?.length || 0) + (data.forms?.length || 0) > 0) s.add("travel-documents");
    return s;
  }, [data]);
  const navAvailable = useMemo(() => {
    const s = new Set(available);
    if (hasActivities) s.add("activities");
    if (hasReservations) s.add("restaurants");
    return s;
  }, [available, hasActivities, hasReservations]);

  // Wave 12.c — section visibility from the server.
  const sectionVisibility = data?._section_visibility || {};
  const lockedSections = data?._locked_sections || [];
  const allSectionsLocked = lockedSections.length === 5; // all five locked → calm preparing-state hero
  // Map server section keys (snake_case) to the SECTION_NAV ids (kebab-case).
  const VIS_TO_NAV_ID = {
    transportation: "transportation",
    stays: "stays",
    balances: "balances",
    agreements: "agreements",
    travel_documents: "travel-documents",
  };
  const visibleSectionIds = useMemo(() => {
    const out = new Set();
    Object.entries(sectionVisibility).forEach(([k, v]) => {
      if (k === "all_unlocked") return;
      if (v && VIS_TO_NAV_ID[k]) out.add(VIS_TO_NAV_ID[k]);
    });
    return out;
  }, [sectionVisibility]);

  if (!trip) return null;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-8 sm:space-y-10 pb-12"
      data-testid="essentials-root"
    >
      {/* Header band */}
      <motion.div variants={fadeInUp}>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Essentials
        </p>
        <h1 className="mt-2 font-serif text-[28px] sm:text-[36px] leading-tight tracking-tight">
          Everything operational, kept calm.
        </h1>
        <p className="mt-3 font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-2xl">
          Transportation, stays, balances, agreements, and travel papers —
          all in one place so the experience itself stays unhurried.
        </p>
      </motion.div>

      {/* Section pills (quick anchor nav) */}
      {data && (
        <motion.div variants={fadeInUp}>
          <SectionPills available={navAvailable} />
        </motion.div>
      )}

      {/* Outstanding items — surfaces at the top, silent when empty */}
      {data?.outstanding_items?.length > 0 && (
        <motion.div variants={fadeInUp}>
          <OutstandingItemsBand items={data.outstanding_items} />
        </motion.div>
      )}

      {/* Loading skeleton */}
      {!data && !error && (
        <motion.div variants={fadeInUp} className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-md bg-white/40 border border-[rgba(0,0,0,0.05)] h-32 animate-pulse" />
          ))}
        </motion.div>
      )}

      {/* Error */}
      {error && (
        <motion.div variants={fadeInUp} className="rounded-md border border-[hsl(var(--border))] bg-white/50 p-6 text-center" data-testid="essentials-error">
          <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
            We couldn't load your essentials right now. Please refresh.
          </p>
        </motion.div>
      )}

      {/* Five shelves — each shelf silent when its data is empty.
          Wave 12.c — also gated by section-level visibility. */}
      {data && (
        <>
          {visibleSectionIds.has("transportation") && available.has("transportation") && (
            <motion.div variants={fadeInUp}>
              <TransportationShelf items={data.transportation} />
            </motion.div>
          )}
          {visibleSectionIds.has("stays") && available.has("stays") && (
            <>
              <motion.div variants={fadeInUp}><EditorialRule /></motion.div>
              <motion.div variants={fadeInUp}>
                <StaysShelf items={data.stays} />
              </motion.div>
            </>
          )}
          {available.has("budget") && (
            <>
              <motion.div variants={fadeInUp}><EditorialRule /></motion.div>
              <motion.div variants={fadeInUp}>
                <BudgetShelf budget={data.budget} tripId={trip?.id} section="main" />
              </motion.div>
            </>
          )}
          <motion.div variants={fadeInUp}>
            <ActivitiesShelf tripId={trip?.id} budget={data.budget} onLoaded={setHasActivities} onReservations={setHasReservations} />
          </motion.div>
          {visibleSectionIds.has("balances") && available.has("balances") && (
            <>
              <motion.div variants={fadeInUp}><EditorialRule /></motion.div>
              <motion.div variants={fadeInUp}>
                <BalancesShelf balances={data.balances} />
              </motion.div>
            </>
          )}
          {visibleSectionIds.has("agreements") && available.has("agreements") && (
            <>
              <motion.div variants={fadeInUp}><EditorialRule /></motion.div>
              <motion.div variants={fadeInUp}>
                <AgreementsShelf items={data.agreements} onChanged={fetchEssentials} />
              </motion.div>
            </>
          )}
          {visibleSectionIds.has("travel-documents") && available.has("travel-documents") && (
            <>
              <motion.div variants={fadeInUp}><EditorialRule /></motion.div>
              <motion.div variants={fadeInUp}>
                <TravelDocumentsShelf
                  documents={data.travel_documents || []}
                  forms={data.forms || []}
                  onChanged={fetchEssentials}
                />
              </motion.div>
            </>
          )}

          {/* Wave 12.c — All sections locked: one calm preparing-state */}
          {allSectionsLocked && (
            <motion.div
              variants={fadeInUp}
              className="rounded-md border border-[hsl(var(--border))] bg-white/50 p-10 text-center"
              data-testid="essentials-all-locked"
            >
              <p className="font-serif text-[22px] tracking-tight">
                A quiet sanctuary, taking shape.
              </p>
              <p className="mt-3 font-accent italic text-[14.5px] text-[hsl(var(--foreground-muted))] max-w-md mx-auto">
                Your advisor is composing the operational details — transportation,
                stays, balances, agreements, travel papers. Each will arrive here
                as it&apos;s ready.
              </p>
            </motion.div>
          )}

          {/* Empty state — when nothing is locked AND nothing is available */}
          {!allSectionsLocked && available.size === 0 && (
            <motion.div variants={fadeInUp} className="rounded-md border border-[hsl(var(--border))] bg-white/50 p-8 text-center" data-testid="essentials-empty">
              <p className="font-serif text-[20px] tracking-tight">A quiet sanctuary, taking shape.</p>
              <p className="mt-3 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
                Your operational details will land here as your advisor confirms each piece.
              </p>
            </motion.div>
          )}
        </>
      )}
    </motion.div>
  );
}
